import React, { useEffect, useState } from "react";
import { api, TIMEFRAMES } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Activity, Layers } from "lucide-react";

const POPULAR = ["EURUSD", "GBPUSD", "USDJPY", "XAUUSD", "BTCUSD", "ETHUSD", "NAS100", "SPX500"];

export default function OrderFlow() {
  const [symbol, setSymbol] = useState("EURUSD");
  const [tf, setTf] = useState("H1");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.orderFlow(symbol, tf, 200);
      setData(res);
    } catch (e) { toast.error("Erreur chargement order flow"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [symbol, tf]);

  const vp = data?.volume_profile;
  const cd = data?.cumulative_delta;
  const ofi = data?.order_flow_imbalance;
  const vwap = data?.vwap;
  const absorption = data?.absorption_events || [];

  return (
    <div className="p-6 space-y-6" data-testid="orderflow-page">
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight">Order Flow & Microstructure</h1>
          <p className="text-white/50 text-sm mt-1">Volume Profile, Delta Cumulé, OFI, VWAP et détection d'absorption.</p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <label className="label">Symbole</label>
            <select className="field w-40" value={symbol} onChange={(e) => setSymbol(e.target.value)} data-testid="of-symbol">
              {POPULAR.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Timeframe</label>
            <select className="field w-24" value={tf} onChange={(e) => setTf(e.target.value)} data-testid="of-tf">
              {TIMEFRAMES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <button className="btn" onClick={load} disabled={loading} data-testid="of-refresh">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
        </div>
      </div>

      {/* Volume Profile */}
      <div className="panel p-5">
        <div className="panel-title mb-4"><Layers size={14} className="inline mr-2" />Volume Profile</div>
        {vp?.ok ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <StatBig label="POC (Point of Control)" value={vp.poc} highlight />
            <StatBig label="VAH — Value Area High" value={vp.vah} />
            <StatBig label="VAL — Value Area Low" value={vp.val} />
            <div className="lg:col-span-3 text-sm text-white/70" data-testid="vp-interpretation">{vp.interpretation}</div>
            <div className="lg:col-span-3">
              <div className="text-xs text-white/50 mb-2">Distribution du volume par prix (top = high, bottom = low)</div>
              <div className="grid gap-0.5" data-testid="vp-histogram">
                {vp.bins?.slice().reverse().map((b, i) => (
                  <div key={i} className="flex items-center gap-2 text-[10px]">
                    <div className="w-16 text-right font-mono text-white/60">{b.price.toFixed(5)}</div>
                    <div className="flex-1 h-3 bg-white/5 rounded-sm relative overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-accent-blue/60" style={{ width: `${Math.min(100, b.volume / Math.max(...vp.bins.map(x => x.volume)) * 100)}%` }} />
                    </div>
                    <div className="w-14 text-right text-white/40">{b.buy_pct}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : <div className="text-white/40">{vp?.reason || "Chargement…"}</div>}
      </div>

      {/* OFI + Cumulative Delta */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="panel p-5">
          <div className="panel-title mb-3">Order Flow Imbalance (OFI)</div>
          {ofi?.ok ? (
            <>
              <div className="font-mono text-4xl font-bold" data-testid="ofi-value">{ofi.ofi > 0 ? "+" : ""}{ofi.ofi}</div>
              <div className={`inline-block mt-2 px-2 py-1 rounded text-xs font-semibold ${
                ofi.signal.includes("STRONG_BUY") ? "bg-up/30 text-up" :
                ofi.signal.includes("BUY") ? "bg-up/20 text-up" :
                ofi.signal.includes("STRONG_SELL") ? "bg-down/30 text-down" :
                ofi.signal.includes("SELL") ? "bg-down/20 text-down" : "bg-white/10 text-white/60"
              }`} data-testid="ofi-signal">{ofi.signal}</div>
              <div className="mt-4">
                <div className="text-xs text-white/50 mb-1">Pression acheteuse vs vendeuse</div>
                <div className="flex h-2 rounded overflow-hidden">
                  <div className="bg-up" style={{ width: `${ofi.buy_pressure_pct}%` }} />
                  <div className="bg-down" style={{ width: `${ofi.sell_pressure_pct}%` }} />
                </div>
                <div className="flex justify-between text-[10px] text-white/50 mt-1">
                  <span>{ofi.buy_pressure_pct}% acheteurs</span>
                  <span>{ofi.sell_pressure_pct}% vendeurs</span>
                </div>
              </div>
              <div className="text-xs text-white/60 mt-3">{ofi.interpretation}</div>
            </>
          ) : <div className="text-white/40">Chargement…</div>}
        </div>

        <div className="panel p-5">
          <div className="panel-title mb-3">Delta Cumulé</div>
          {cd?.ok ? (
            <>
              <div className={`font-mono text-4xl font-bold ${cd.cumulative_delta >= 0 ? "text-up" : "text-down"}`} data-testid="cum-delta">
                {cd.cumulative_delta > 0 ? "+" : ""}{cd.cumulative_delta.toLocaleString()}
              </div>
              <div className="text-sm text-white/60 mt-2">Delta des 20 dernières bougies : <b>{cd.last_20_delta > 0 ? "+" : ""}{cd.last_20_delta.toLocaleString()}</b></div>
              {cd.divergence && (
                <div className={`mt-3 p-3 rounded text-xs ${cd.divergence.type === "bearish" ? "bg-down/20 text-down" : "bg-up/20 text-up"}`} data-testid="delta-divergence">
                  ⚠️ Divergence {cd.divergence.type} : {cd.divergence.warning}
                </div>
              )}
              <div className="mt-4 flex items-end gap-0.5 h-16" data-testid="cum-delta-sparkline">
                {cd.delta_curve?.slice(-40).map((v, i) => {
                  const max = Math.max(...cd.delta_curve.map(Math.abs));
                  const h = Math.abs(v) / max * 100;
                  return <div key={i} className={`flex-1 ${v >= 0 ? "bg-up/50" : "bg-down/50"}`} style={{ height: `${h}%` }} />;
                })}
              </div>
            </>
          ) : <div className="text-white/40">Chargement…</div>}
        </div>
      </div>

      {/* VWAP + Absorption */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="panel p-5">
          <div className="panel-title mb-3">VWAP & Bandes σ</div>
          {vwap?.ok ? (
            <div className="space-y-2 text-sm" data-testid="vwap-panel">
              <Row label="Prix actuel" value={vwap.last_close?.toFixed(5)} mono />
              <Row label="VWAP" value={vwap.vwap?.toFixed(5)} mono highlight />
              <Row label="+1σ" value={vwap.upper_1sigma?.toFixed(5)} mono muted />
              <Row label="+2σ" value={vwap.upper_2sigma?.toFixed(5)} mono muted />
              <Row label="-1σ" value={vwap.lower_1sigma?.toFixed(5)} mono muted />
              <Row label="-2σ" value={vwap.lower_2sigma?.toFixed(5)} mono muted />
              <Row label="Z-score" value={vwap.z_score} mono highlight />
              <div className={`text-xs mt-3 p-2 rounded ${
                Math.abs(vwap.z_score) > 2 ? "bg-warn/20 text-warn" : "bg-white/5 text-white/60"
              }`}>{vwap.interpretation}</div>
            </div>
          ) : <div className="text-white/40">Chargement…</div>}
        </div>

        <div className="panel p-5">
          <div className="panel-title mb-3"><Activity size={14} className="inline mr-2" />Absorption (Smart Money)</div>
          <div data-testid="absorption-events">
            {absorption.length === 0 ? (
              <div className="text-white/40 text-sm">Aucun événement d'absorption détecté récemment.</div>
            ) : (
              <div className="space-y-2">
                {absorption.slice().reverse().map((e, i) => (
                  <div key={i} className="p-3 bg-white/5 rounded flex justify-between items-center">
                    <div>
                      <div className={`text-sm font-semibold ${e.direction === "bullish" ? "text-up" : "text-down"}`}>
                        {e.direction === "bullish" ? "▲ Absorption haussière" : "▼ Absorption baissière"}
                      </div>
                      <div className="text-xs text-white/50 mt-1">@ {e.price.toFixed(5)}</div>
                    </div>
                    <div className="text-right text-xs">
                      <div>Vol × {e.volume_ratio}</div>
                      <div className="text-white/50">Range × {e.range_ratio}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatBig({ label, value, highlight }) {
  return (
    <div className={`p-4 rounded ${highlight ? "bg-accent-blue/10 border border-accent-blue/30" : "bg-white/5"}`}>
      <div className="text-xs text-white/50 uppercase tracking-widest">{label}</div>
      <div className="font-mono text-2xl mt-1 font-bold">{value}</div>
    </div>
  );
}

function Row({ label, value, mono, highlight, muted }) {
  return (
    <div className="flex justify-between items-center py-1 border-b border-white/5 last:border-0">
      <span className={`text-xs ${muted ? "text-white/40" : "text-white/60"}`}>{label}</span>
      <span className={`${mono ? "font-mono" : ""} ${highlight ? "font-bold text-accent-blue" : ""}`}>{value}</span>
    </div>
  );
}
